/**
 * Com'è fatta l'email che riceve l'ufficio, e come cambiarla (punto 6c).
 *
 * Terzo capo della stessa cosa: «Server di posta» dice *da dove* parte,
 * «Avvisi automatici» *quando*, questa *com'è fatta*.
 *
 * Tre modi di guardarla, e servono tutti e tre:
 *
 *  - **l'anteprima nel riquadro** non manda niente e si ridisegna a ogni
 *    ritocco: è quella con cui si sceglie;
 *  - **il testo semplice** è la versione che parte insieme all'altra dentro
 *    lo stesso messaggio, e va guardata perché tre dei sei campi finiscono
 *    anche lì;
 *  - **l'invio a sé stessi** è l'unica prova che conta davvero, perché mostra
 *    come rende il client di posta vero — Outlook impagina le email col motore
 *    di Word e non sempre fa quello che fa il browser.
 *
 * ## Il confine è scritto nella pagina, non lasciato intuire
 *
 * Strada A del piano: sono modificabili **le parti, non la struttura**. Un
 * editor che sembra permettere tutto e poi ignora metà di ciò che scrivi è
 * peggio di uno che dichiara i suoi sei campi — quindi la pagina dice sia
 * cosa si può cambiare, sia cosa no, sia quali campi valgono anche per la
 * versione in solo testo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErroreApi } from '../../api/client.ts';
import { notifiche as apiNotifiche } from '../../api/scadenziario.ts';
import type { AnteprimaAvviso, Aspetto, AspettoSalvato } from '../../api/tipi-dati.ts';
import { useAutenticazione } from '../../auth/contesto.tsx';
import { useConfigurazioneSmtp } from './configurazione.ts';

/**
 * I sei campi, con quello che serve a disegnarli.
 *
 * In una tabella e non sei blocchi copiati: il limite di caratteri deve
 * essere lo stesso del database e dello schema Zod, e tre elenchi separati
 * divergono. `dove` è ciò che rende visibile il confine — quali parole
 * arrivano anche a chi legge la posta senza HTML.
 */
const CAMPI: {
  chiave: Exclude<keyof Aspetto, 'coloreMarchio'>;
  etichetta: string;
  max: number;
  righe?: number;
  facoltativo?: boolean;
  aiuto: string;
  dove: 'ENTRAMBE' | 'SOLO_GRAFICA';
}[] = [
  {
    chiave: 'marchioNome',
    etichetta: 'Nome in testata',
    max: 60,
    aiuto: 'Grande, in cima al messaggio, sulla fascia colorata.',
    dove: 'SOLO_GRAFICA',
  },
  {
    chiave: 'marchioSottotitolo',
    etichetta: 'Sottotitolo in testata',
    max: 80,
    facoltativo: true,
    aiuto: 'Piccolo, accanto al nome. Lasciandolo vuoto non compare.',
    dove: 'SOLO_GRAFICA',
  },
  {
    chiave: 'saluto',
    etichetta: 'Saluto',
    max: 60,
    aiuto: 'Il nome di chi riceve lo aggiunge il programma: «Buongiorno» diventa «Buongiorno Anna,».',
    dove: 'ENTRAMBE',
  },
  {
    chiave: 'fraseApertura',
    etichetta: "Frase d'apertura",
    max: 300,
    righe: 2,
    facoltativo: true,
    aiuto:
      'Una riga vostra sotto all\'apertura. La frase che conta — «Ci sono 3 scadenze che ti ' +
      'riguardano» — la scrive il programma coi numeri del giorno e non è modificabile.',
    dove: 'ENTRAMBE',
  },
  {
    chiave: 'testoPulsante',
    etichetta: 'Testo del pulsante',
    max: 60,
    aiuto: "Il pulsante che riporta all'applicazione. Nel testo semplice c'è l'indirizzo per esteso.",
    dove: 'SOLO_GRAFICA',
  },
  {
    chiave: 'piePagina',
    etichetta: 'Piè di pagina',
    max: 600,
    righe: 4,
    aiuto: "Il paragrafo in fondo, sopra all'indirizzo dell'applicazione.",
    dove: 'ENTRAMBE',
  },
];

/** Due aspetti sono uguali? Serve a sapere se ci sono modifiche non salvate. */
function uguali(a: Aspetto, b: Aspetto): boolean {
  return (
    CAMPI.every((c) => a[c.chiave] === b[c.chiave]) && a.coloreMarchio === b.coloreMarchio
  );
}

/** Toglie i campi in più da `AspettoSalvato`, lasciando solo l'aspetto. */
function soloAspetto(s: AspettoSalvato): Aspetto {
  const { modificataIl: _i, modificataDa: _d, salvato: _s, ...aspetto } = s;
  return aspetto;
}

export function AspettoAvvisi() {
  const { utente } = useAutenticazione();
  const { config, errore: erroreConfig } = useConfigurazioneSmtp();

  /** Quello che c'è nel database. Il paragone per «ci sono modifiche». */
  const [salvato, setSalvato] = useState<AspettoSalvato | null>(null);
  /** Quello che c'è nei campi adesso. */
  const [bozza, setBozza] = useState<Aspetto | null>(null);

  const [anteprima, setAnteprima] = useState<AnteprimaAvviso | null>(null);
  const [vista, setVista] = useState<'GRAFICA' | 'TESTO'>('GRAFICA');
  const [errore, setErrore] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);
  const [inSalvataggio, setInSalvataggio] = useState(false);

  const [inInvio, setInInvio] = useState(false);
  const [esitoInvio, setEsitoInvio] = useState<{ ok: boolean; messaggio: string } | null>(null);

  useEffect(() => {
    let annullato = false;

    apiNotifiche
      .aspetto()
      .then((r) => {
        if (annullato) return;
        setSalvato(r);
        setBozza(soloAspetto(r));
      })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      });

    return () => { annullato = true; };
  }, []);

  /*
   * L'anteprima segue i campi, con un respiro.
   *
   * ⚠️ Il ritardo non è una rifinitura: senza, ogni lettera digitata nel piè
   * di pagina sarebbe una richiesta al server e un riquadro che sfarfalla
   * mentre si scrive. 400 ms è il tempo in cui si smette di digitare per
   * guardare cosa si è scritto.
   */
  const primoDisegno = useRef(true);

  useEffect(() => {
    if (bozza === null) return;

    let annullato = false;
    const attesa = primoDisegno.current ? 0 : 400;
    primoDisegno.current = false;

    const tempo = setTimeout(() => {
      apiNotifiche
        .anteprima(bozza)
        .then((r) => { if (!annullato) setAnteprima(r); })
        .catch((e: unknown) => {
          // Un colore scritto male mentre si digita fa rispondere 400 al
          // server: è un errore di battitura in corso, non un guasto, e
          // riempire la pagina di rosso a ogni carattere sarebbe rumore.
          // L'anteprima resta all'ultima buona finché non torna valida.
          if (!annullato && !(e instanceof ErroreApi)) {
            setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
          }
        });
    }, attesa);

    return () => { annullato = true; clearTimeout(tempo); };
  }, [bozza]);

  const cambia = useCallback((chiave: keyof Aspetto, valore: string) => {
    setConferma(null);
    setBozza((b) => (b === null ? b : { ...b, [chiave]: valore }));
  }, []);

  async function salva() {
    if (bozza === null) return;
    setErrore(null);
    setConferma(null);
    setInSalvataggio(true);

    try {
      const r = await apiNotifiche.salvaAspetto(bozza);
      setSalvato(r);
      setBozza(soloAspetto(r));
      setConferma('Salvato. Vale dal prossimo invio, senza riavviare niente.');
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInSalvataggio(false);
    }
  }

  async function ripristina() {
    setErrore(null);
    setConferma(null);
    setInSalvataggio(true);

    try {
      const r = await apiNotifiche.ripristinaAspetto();
      setSalvato(r);
      setBozza(soloAspetto(r));
      setConferma("Rimessi i valori di fabbrica, quelli con cui l'applicazione è nata.");
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInSalvataggio(false);
    }
  }

  async function inviaAMe() {
    setEsitoInvio(null);
    setErrore(null);
    setInInvio(true);

    try {
      // Si manda la bozza, non il salvato: la prova deve mostrare quello che
      // si sta guardando. Vedi il commento sull'API.
      const r = await apiNotifiche.inviaAnteprima(bozza ?? undefined);
      setEsitoInvio(r);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInInvio(false);
    }
  }

  if (utente === null) return null;
  const attivo = config?.attivo ?? false;
  const modificato = salvato !== null && bozza !== null && !uguali(bozza, soloAspetto(salvato));

  return (
    <div className="pannello pannello-imbottito">
      <h2 className="sezione-titolo">Aspetto degli avvisi</h2>
      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>
        Questo è il messaggio che si troveranno in casella i colleghi, con scadenze
        d'esempio: sempre le stesse, così una differenza fra un giorno e l'altro viene di
        sicuro dalla grafica e non dai dati. Guardarlo qui non manda niente a nessuno.
      </p>

      {erroreConfig !== null && <div className="avviso-errore" role="alert">{erroreConfig}</div>}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}
      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>Chiudi</button>
        </div>
      )}

      {bozza === null || anteprima === null ? (
        <p className="stato-caricamento">Caricamento…</p>
      ) : (
        <>
          <div className="editor-avviso">
            <form
              className="editor-avviso-campi"
              onSubmit={(e) => { e.preventDefault(); void salva(); }}
            >
              {/*
                Il confine dichiarato, non lasciato intuire: è la conseguenza
                della strada A scritta nel piano. Chi cerca «come sposto le
                scadenze» deve trovare qui la risposta, non provarci e basta.
              */}
              <p className="aiuto nota-confine">
                Si cambiano <strong>le parole e il colore</strong>. L'impaginazione delle
                scadenze, l'ordine delle sezioni e i colori delle quattro situazioni
                (scaduta, ultima chiamata, in scadenza, quadro) restano come sono: sono il
                segnale di urgenza, ed è quello che rende l'avviso leggibile in due secondi.
                <br />
                Sono <strong>parole, non HTML</strong>: i segni <code>&lt;</code> e{' '}
                <code>&gt;</code> vengono rifiutati. È il motivo per cui da qui non si può
                rompere un'email — al massimo farla brutta.
              </p>

              {CAMPI.map((c) => (
                <div className="campo" key={c.chiave}>
                  <label htmlFor={`a-${c.chiave}`}>
                    {c.etichetta}{' '}
                    {c.facoltativo === true && <span className="tenue">facoltativo</span>}
                    {c.dove === 'ENTRAMBE' && (
                      <span className="segno-entrambe" title="Vale anche per la versione in solo testo">
                        anche nel testo semplice
                      </span>
                    )}
                  </label>

                  {c.righe === undefined ? (
                    <input
                      id={`a-${c.chiave}`}
                      type="text"
                      value={bozza[c.chiave]}
                      maxLength={c.max}
                      onChange={(e) => cambia(c.chiave, e.target.value)}
                    />
                  ) : (
                    <textarea
                      id={`a-${c.chiave}`}
                      rows={c.righe}
                      value={bozza[c.chiave]}
                      maxLength={c.max}
                      onChange={(e) => cambia(c.chiave, e.target.value)}
                    />
                  )}

                  <p className="aiuto">
                    {c.aiuto}{' '}
                    <span className="tenue">{bozza[c.chiave].length}/{c.max}</span>
                  </p>
                </div>
              ))}

              <div className="campo">
                <label htmlFor="a-colore">Colore del marchio</label>
                <div className="campo-affiancato">
                  <input
                    id="a-colore"
                    type="color"
                    className="scelta-colore"
                    value={bozza.coloreMarchio}
                    onChange={(e) => cambia('coloreMarchio', e.target.value)}
                  />
                  <input
                    type="text"
                    aria-label="Colore del marchio in esadecimale"
                    value={bozza.coloreMarchio}
                    maxLength={7}
                    style={{ minWidth: '110px', fontFamily: 'var(--mono)' }}
                    onChange={(e) => cambia('coloreMarchio', e.target.value.toLowerCase())}
                  />
                </div>
                <p className="aiuto">
                  Testata, pulsante e collegamenti. Gli altri toni si calcolano da questo,
                  compreso <strong>se il testo sopra debba essere bianco o nero</strong>: è
                  quello che impedisce a un colore chiaro di produrre una testata che non si
                  legge. I colori delle quattro situazioni non cambiano.
                </p>
              </div>

              <div className="dialogo-azioni">
                <button
                  type="submit"
                  className="bottone bottone-principale"
                  disabled={inSalvataggio || !modificato}
                >
                  {inSalvataggio ? 'Salvataggio…' : modificato ? 'Salva' : 'Salvato'}
                </button>
                <button
                  type="button"
                  className="bottone"
                  onClick={() => void ripristina()}
                  disabled={inSalvataggio}
                >
                  Rimetti i valori di fabbrica
                </button>
              </div>

              <p className="aiuto tracciabilita">
                {modificato
                  ? "Le modifiche si vedono già nell'anteprima, ma non sono in vigore finché non si salva."
                  : salvato?.salvato === true && salvato.modificataDa !== null
                    ? `Modificato da ${salvato.modificataDa}.`
                    : "Nessuno l'ha ancora cambiato: sono i valori di fabbrica."}
              </p>
            </form>

            <div className="editor-avviso-anteprima">
              <div className="scelta-vista" role="group" aria-label="Come guardare l'anteprima">
                <button
                  type="button"
                  className="bottone"
                  aria-pressed={vista === 'GRAFICA'}
                  onClick={() => setVista('GRAFICA')}
                >
                  Grafica
                </button>
                <button
                  type="button"
                  className="bottone"
                  aria-pressed={vista === 'TESTO'}
                  onClick={() => setVista('TESTO')}
                >
                  Testo semplice
                </button>
                <span className="aiuto">
                  {vista === 'GRAFICA'
                    ? 'Quella che vedranno quasi tutti.'
                    : "Parte insieme all'altra, nello stesso messaggio: la vede chi legge la posta senza HTML."}
                </span>
              </div>

              <div className="anteprima-avviso">
                <div className="anteprima-testata">
                  <span className="tenue">Oggetto:</span> {anteprima.oggetto}
                </div>

                {vista === 'GRAFICA' ? (
                  /*
                   * In un riquadro isolato, non innestato nella pagina: quello è un
                   * documento HTML intero, con il suo `<body>` e i suoi colori, e
                   * incollarlo qui dentro gli farebbe ereditare gli stili
                   * dell'applicazione — vedremmo un'anteprima che mente.
                   *
                   * `sandbox` vuoto è il grado massimo: niente script, niente form,
                   * niente accesso a questa pagina. ⚠️ Dal 6c conta più di prima:
                   * qui dentro finiscono parole scritte da un amministratore. Il
                   * backend le rifiuta se contengono marcatura e le passa comunque
                   * per `esc()`, ma questa è la terza barriera e non si toglie —
                   * `dangerouslySetInnerHTML` qui sarebbe uno `<script>` salvato da
                   * un amministratore ed eseguito nel browser di un altro.
                   */
                  <iframe
                    title="Anteprima dell'avviso"
                    className="anteprima-telaio"
                    srcDoc={anteprima.html}
                    sandbox=""
                  />
                ) : (
                  <pre className="anteprima-testo">{anteprima.testo}</pre>
                )}
              </div>

              <h3 className="sezione-titolo" style={{ marginTop: 24 }}>Provala nella tua casella</h3>
              <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 12 }}>
                Il riquadro qui sopra è il browser, non un programma di posta: Outlook impagina
                le email in modo suo e l'unico modo di sapere come vengono è riceverne una.
                Arriva <strong>solo a te</strong>, con l'oggetto marcato «[Anteprima]», e parte
                <strong> com'è adesso a schermo</strong> anche se non hai ancora salvato.
              </p>

              {esitoInvio !== null && (
                <div className={esitoInvio.ok ? 'avviso-esito' : 'avviso-errore'} role="status">
                  {esitoInvio.messaggio}
                </div>
              )}

              <div className="campo-affiancato">
                <button
                  type="button"
                  className="bottone"
                  onClick={() => void inviaAMe()}
                  disabled={inInvio || !attivo}
                >
                  {inInvio ? 'Invio…' : `Mandala a ${utente.email}`}
                </button>
                <span className="aiuto">
                  {attivo ? (
                    "È un invio vero dalla casella aziendale. Se non arriva, leggi qui l'errore prima di ripremere."
                  ) : (
                    <>Disponibile quando l'invio è attivo: si accende da <Link to="/impostazioni/posta">Server di posta</Link>.</>
                  )}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
